import React, { useEffect, useState } from 'react';
import { useWorldStore } from '../stores/worldStore';
import {
  canShareToScreens, canStopScreen, COUNCIL_PATH, COUNCIL_PERK_NOTICE, describeAllowance, fetchScreenAccess, isScreenLinkMember,
  openDesktopShare, ScreenShareError, shareToScreen, stopScreen, useRoomScreenStore,
} from '../services/roomScreens';
import { canCaptureScreen, isPresenting, startScreenShare } from '../services/screenShareService';
import './MediaPanels.css';

/**
 * Desktop "Share link" for a room screen: same flow as the VR keyboard panel.
 * Share link is a Creator Council perk: non-members see a notice (with a
 * link to /council) instead of the link field; Share my screen stays free.
 */
export default function RoomScreenShareDialog({ isOwner = false }: { isOwner?: boolean }) {
  const target = useRoomScreenStore((s) => s.desktopShareTarget);
  const screen = useRoomScreenStore((s) => (target ? s.screens[target] : undefined));
  const configured = useRoomScreenStore((s) => s.configured);
  const item = useWorldStore((s) => (target ? s.screenProps[target] : undefined));
  const myId = useWorldStore((s) => s.user?.id || null);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorCode, setErrorCode] = useState<string | undefined>();
  const access = useRoomScreenStore((s) => s.access);
  useEffect(() => { if (target) void fetchScreenAccess(); }, [target]);
  if (!target || !item) return null;
  const locked = !!access && !isScreenLinkMember(access);
  const allowance = describeAllowance(access);
  const close = () => { openDesktopShare(null); setError(''); setErrorCode(undefined); setUrl(''); };
  const signedIn = canShareToScreens();
  const go = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(''); setErrorCode(undefined);
    try { await shareToScreen(target, url, { width: item.width, height: item.height }); close(); }
    catch (err) { setError(err instanceof Error ? err.message : 'Could not share'); setErrorCode(err instanceof ScreenShareError ? err.code : undefined); }
    finally { setBusy(false); }
  };
  const liveShare = useWorldStore.getState().activeScreenShare;
  const shareScreen = async () => {
    setBusy(true); setError('');
    try {
      if (screen) {
        if (!canStopScreen(screen, isOwner, myId)) throw new Error('Someone else’s link is playing on this screen. Pick an empty screen, or ask them to stop it.');
        await stopScreen(target);
      }
      await startScreenShare();
      close();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not share your screen.';
      if (!/permission denied|notallowed|cancel/i.test(msg)) setError(msg);
    } finally { setBusy(false); }
  };
  return <div className="modal-overlay" onClick={close}>
    <div className="modal-content upload-modal" role="dialog" aria-modal="true" aria-labelledby="room-share-title" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header"><h2 id="room-share-title">Share to {item.name || 'the screen'}</h2><button className="close-button" aria-label="Close" onClick={close}>×</button></div>
      <form className="panel-picker" onSubmit={go}>
        {screen && <p className="room-share-now">Now showing: {screen.url}{screen.startedByName ? ` (shared by ${screen.startedByName})` : ''}</p>}
        {signedIn && locked ? <>
          <label>Share link <span className="room-share-badge">Council</span></label>
          <div className="room-share-perk" role="note">
            <strong>{COUNCIL_PERK_NOTICE.split('. Tip:')[0]}.</strong>
            <p>{canCaptureScreen() ? 'Tip: open the video in a browser tab and use Share my screen below. Everyone sees it, including headsets.' : 'Tip: open the video in a tab on a desktop browser and use Share my screen. Everyone sees it, including headsets.'}</p>
            <p><a href={COUNCIL_PATH} target="_blank" rel="noopener">See Creator Council plans</a></p>
          </div>
          {screen && canStopScreen(screen, isOwner, myId) && <div className="room-share-quick">
            <button type="button" onClick={() => { void stopScreen(target).then(close).catch((err) => setError(err.message)); }}>Stop sharing</button>
          </div>}
          {error && <p className="panel-picker-error" role="alert">{error}</p>}
        </> : <>
          <label htmlFor="room-share-url">YouTube, a website, or a video file link</label>
          <input id="room-share-url" autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=…" required disabled={!signedIn} />
          <div className="room-share-quick">
            <button type="button" onClick={() => setUrl('https://www.youtube.com')}>YouTube</button>
            <button type="button" onClick={() => setUrl('')}>Clear</button>
            {screen && canStopScreen(screen, isOwner, myId) && <button type="button" onClick={() => { void stopScreen(target).then(close).catch((err) => setError(err.message)); }}>Stop sharing</button>}
          </div>
          <p>Everyone in the room sees and hears the same page, in VR too. Anyone can click and scroll it.</p>
          {allowance && <p className="room-share-allowance">{allowance}</p>}
          {configured === false && <p>The cloud browser isn't set up yet. Video file links (.mp4, .webm, .m3u8) still play in sync for everyone.</p>}
          {!signedIn && <p className="panel-picker-error" role="alert">Sign in to share to the screen.</p>}
          {error && <p className="panel-picker-error" role="alert">{error}{errorCode === 'council_only' && <> <a href={COUNCIL_PATH} target="_blank" rel="noopener">See Creator Council plans</a></>}</p>}
          <button className="btn-primary" type="submit" disabled={busy || !signedIn}>{busy ? 'Starting…' : 'Share to screen'}</button>
        </>}
        {canCaptureScreen() && <div className="room-share-own">
          <p><strong>Or share your own screen.</strong> Show a tab, a window or your whole desktop: slides, a game, or YouTube playing on your computer. To share sound, pick a <em>Chrome tab</em> and tick “Share tab audio”. Everyone sees it on the screens, in VR too.</p>
          <button type="button" className="btn-glass" disabled={busy || isPresenting() || (!!liveShare && !isPresenting())} onClick={() => void shareScreen()}>
            {isPresenting() ? 'You’re sharing your screen' : liveShare ? 'Someone is already sharing' : 'Share my screen'}
          </button>
        </div>}
      </form>
    </div>
  </div>;
}
